/**
 * Edition Browser - Browse all printings of a card
 *
 * Shows all available printings (editions) of a card name, allowing users to
 * browse different versions. Owned printings show a badge with quantity.
 *
 * Used in:
 * - Collection card details (to change edition)
 * - List card details (to change edition)
 * - Starred card details (to change edition)
 * - Set card details
 */
import clsx from 'clsx';
import type { ReactElement } from 'react';
import { useMemo, useState } from 'react';
import { IoCheckmarkCircle, IoChevronDown, IoChevronUp, IoLayersOutline } from 'react-icons/io5';

import CardHoverPreview from '@/components/CardHoverPreview/CardHoverPreview';
import CardImage from '@/components/CardImage/CardImage';
import useCardIndex from '@/hooks/useCardIndex';
import type { MinimalCard } from '@/services/cardDatabase';

export interface EditionBrowserProps {
  /** Card name to browse editions for */
  cardName: string;
  /** Currently selected scryfall_id (to highlight) */
  selectedId?: string;
  /** Map of scryfall_id to owned quantity (optional) */
  ownedQuantities?: Map<string, number>;
  /** Callback when an edition is selected */
  onSelect?: (card: MinimalCard) => void;
  /** Whether selection is enabled (default: true) */
  selectable?: boolean;
  /** Custom label for the header (default: "All Editions") */
  label?: string;
  /** Whether to start expanded (default: false) - useful for modal context */
  defaultExpanded?: boolean;
  /** Whether to use compact padding (no outer padding) - useful for modal context */
  compact?: boolean;
}

/** Number of editions to show before "Load more" */
const INITIAL_DISPLAY_COUNT = 12;
/** Additional editions to load on "Load more" */
const LOAD_MORE_COUNT = 12;

interface EditionCardProps {
  card: MinimalCard;
  isSelected: boolean;
  ownedQuantity: number;
  onSelect?: () => void;
  selectable: boolean;
}

function EditionCard({ card, isSelected, ownedQuantity, onSelect, selectable }: EditionCardProps): ReactElement {
  const isOwned = ownedQuantity > 0;

  return (
    <button
      type="button"
      onClick={selectable ? onSelect : undefined}
      disabled={!selectable}
      className={clsx(
        'group relative flex flex-col rounded-lg p-1.5 transition-all',
        selectable && 'hover:bg-slate/70 cursor-pointer',
        !selectable && 'cursor-default',
      )}
      aria-label={`${card.set.toUpperCase()} #${card.collector_number}${isOwned ? ` (owned: ${ownedQuantity})` : ''}`}
      aria-pressed={isSelected}
    >
      {/* Card Image with Hover Preview */}
      <CardHoverPreview imageUris={card.image_uris} name={card.name}>
        <div className="relative rounded">
          <CardImage imageUris={card.image_uris} name={card.name} size="small" />

          {/* Owned Badge */}
          {isOwned && (
            <div className="bg-success/90 absolute top-0 right-0 flex items-center gap-0.5 rounded-tr rounded-bl px-1 py-0.5">
              <IoCheckmarkCircle className="text-pearl h-3 w-3" />
              {ownedQuantity > 1 && <span className="text-pearl text-[10px] font-medium">{ownedQuantity}</span>}
            </div>
          )}

          {/* Selection indicator overlay */}
          {isSelected && <div className="ring-primary pointer-events-none absolute inset-0 rounded ring-2 ring-amber-300 ring-inset" />}
        </div>
      </CardHoverPreview>

      {/* Set Info */}
      <div className="mt-1.5 flex items-center justify-center gap-1">
        <span className="bg-storm text-silver rounded px-1 font-mono text-[10px] font-medium uppercase">{card.set}</span>
        <span className="text-ash text-[10px]">#{card.collector_number}</span>
      </div>
    </button>
  );
}

export default function EditionBrowser({
  cardName,
  selectedId,
  ownedQuantities = new Map(),
  onSelect,
  selectable = true,
  label = 'All Editions',
  defaultExpanded = false,
  compact = false,
}: EditionBrowserProps): ReactElement | null {
  const { getAllPrintingsByName } = useCardIndex();
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);
  const [displayCount, setDisplayCount] = useState(INITIAL_DISPLAY_COUNT);

  // Get all printings of this card, sorted by set code (newest sets tend to have later codes)
  const allPrintings = useMemo(() => {
    if (!cardName) return [];
    const printings = getAllPrintingsByName(cardName);
    // Sort by set code descending (rough proxy for recency)
    return [...printings].sort((a, b) => b.set.localeCompare(a.set));
  }, [cardName, getAllPrintingsByName]);

  const totalCount = allPrintings.length;
  const displayedPrintings = allPrintings.slice(0, displayCount);
  const hasMore = displayCount < totalCount;

  // Count how many editions are owned
  const ownedEditionCount = useMemo(() => {
    let count = 0;
    for (const printing of allPrintings) {
      if (ownedQuantities.has(printing.id)) {
        count++;
      }
    }
    return count;
  }, [allPrintings, ownedQuantities]);

  const handleLoadMore = () => {
    setDisplayCount((prev) => Math.min(prev + LOAD_MORE_COUNT, totalCount));
  };

  // Don't render if only one printing (no browsing needed)
  if (totalCount <= 1) {
    return null;
  }

  return (
    <section className={compact ? '' : 'px-6 pb-6'}>
      <div className="bg-slate/50 border-storm rounded-xl border">
        {/* Collapsible Header */}
        <button
          type="button"
          onClick={() => setIsExpanded(!isExpanded)}
          className="flex w-full items-center justify-between p-4 text-left transition-colors"
          aria-expanded={isExpanded}
        >
          <div className="flex items-center gap-2">
            <IoLayersOutline className="text-ash h-4 w-4" />
            <span className="text-ash text-xs font-medium tracking-widest uppercase">{label}</span>
            <span className="bg-slate text-silver rounded-full px-2 py-0.5 text-[10px] font-medium">{totalCount}</span>
            {ownedEditionCount > 0 && (
              <span className="bg-success/20 text-success rounded-full px-2 py-0.5 text-[10px] font-medium">{ownedEditionCount} owned</span>
            )}
          </div>
          <span className="text-ash">{isExpanded ? <IoChevronUp className="h-4 w-4" /> : <IoChevronDown className="h-4 w-4" />}</span>
        </button>

        {/* Expanded Content */}
        {isExpanded && (
          <div className="border-storm border-t px-4 pt-3 pb-4">
            {/* Edition Grid */}
            <div className="flex flex-wrap gap-x-4 gap-y-1">
              {displayedPrintings.map((card) => (
                <EditionCard
                  key={card.id}
                  card={card}
                  isSelected={card.id === selectedId}
                  ownedQuantity={ownedQuantities.get(card.id) ?? 0}
                  onSelect={() => onSelect?.(card)}
                  selectable={selectable}
                />
              ))}
            </div>

            {/* Load More */}
            {hasMore && (
              <button
                type="button"
                onClick={handleLoadMore}
                className="text-ash hover:text-silver mt-4 flex w-full items-center justify-center gap-1 text-xs transition-colors"
              >
                <IoChevronDown className="h-3 w-3" />
                Show {Math.min(LOAD_MORE_COUNT, totalCount - displayCount)} more editions
              </button>
            )}
          </div>
        )}
      </div>
    </section>
  );
}
